// @ts-check
/**
 * @typedef {Object} ffSliderCustomProperties
 * @property {Function} ffRestrains Removes empty spaces generated for the arrows and dots if they aren't being displayed.
 * @property {Function} ffResizeSlides
 * @property {() => Promise<void>} ffInit
 * @property {{
 *   update: Function,
 *   destroy: Function,
 *   updateSlides: Function,
 *   appendSlide: Function,
 *   removeAllSlides: Function,
 *   removeSlide: Function,
 *   on: (
 *     eventName:
 *       | "activeIndexChange"
 *       | "afterInit"
 *       | "beforeDestroy"
 *       | "beforeInit"
 *       | "beforeLoopFix"
 *       | "beforeResize"
 *       | "beforeSlideChangeStart"
 *       | "beforeTransitionStart"
 *       | "breakpoint"
 *       | "changeDirection"
 *       | "click"
 *       | "destroy"
 *       | "doubleClick"
 *       | "doubleTap"
 *       | "fromEdge"
 *       | "init"
 *       | "lock"
 *       | "loopFix"
 *       | "momentumBounce"
 *       | "observerUpdate"
 *       | "orientationchange"
 *       | "progress"
 *       | "reachBeginning"
 *       | "reachEnd"
 *       | "realIndexChange"
 *       | "resize"
 *       | "setTransition"
 *       | "setTranslate"
 *       | "slideChange"
 *       | "slideChangeTransitionEnd"
 *       | "slideChangeTransitionStart"
 *       | "slideNextTransitionEnd"
 *       | "slideNextTransitionStart"
 *       | "slidePrevTransitionEnd"
 *       | "slidePrevTransitionStart"
 *       | "slideResetTransitionEnd"
 *       | "slideResetTransitionStart"
 *       | "sliderFirstMove"
 *       | "sliderMove"
 *       | "slidesGridLengthChange"
 *       | "slidesLengthChange"
 *       | "slidesUpdated"
 *       | "snapGridLengthChange"
 *       | "snapIndexChange"
 *       | "tap"
 *       | "toEdge"
 *       | "touchEnd"
 *       | "touchMove"
 *       | "touchMoveOpposite"
 *       | "touchStart"
 *       | "transitionEnd"
 *       | "transitionStart"
 *       | "unlock"
 *       | "update",
 *     callback: Function
 *   ) => void
 * }} ffSwiper
 *
 * @typedef {ffSliderCustomProperties & HTMLElement} FfSlider
 */

if (!customElements.get("old-theme-ff-slider")) {
  class FfSlider extends HTMLElement {
    constructor() {
      super();

      const {
        ffSwiperSelector,
        ffMobileItemsPerView,
        ffDesktopItemsPerView,
        ffMobileBreakpoint,
        ffLeftArrowClass,
        ffRightArrowClass,
        ffDotsClass,
        ffDotClass,
        ffDotActiveClass,
        ffDisabledArrowClass,
        ffMobileSlidesGap,
        ffDesktopSlidesGap,
        ffNavigationType,
        ffIsVertical,
        ffDesktopAutoplay,
        ffDesktopAutoplayDelay,
        ffMobileAutoplay,
        ffMobileAutoplayDelay,
        ffMobileDisabled,
        ffDesktopDisabled,
        ffNotHideNav,
        ffResizeSlides,
        ffDesktopThumbsSelector,
        ffMobileThumbsSelector,
        ffCenteredSlides,
      } = this.dataset;

      /**
       * @type {HTMLElement}
       */
      this.ffSlider = (() => {
        /**
         * @type {HTMLElement | null}
         *`
         */
        const ffSlider = this.querySelector(ffSwiperSelector || "");

        if (ffSlider) {
          return ffSlider;
        } else {
          throw new Error(
            `There wasn't found an element with the selector: '${this.dataset.ffSwiperSelector}, which is needed for the creation of the swiper instance.`
          );
        }
      })();

      this.ffMobileBreakpoint = ffMobileBreakpoint;

      this.ffMobileDisabled = ffMobileDisabled;
      this.ffDesktopDisabled = ffDesktopDisabled;

      this.ffShouldResizeSlides = ffResizeSlides;

      this.ffNotHideNav = ffNotHideNav;
      /**
       * @type {string | "auto"}
       */
      this.ffMobileItemsPerView = ffMobileItemsPerView || "auto";
      /**
       * @type {string | "auto"}
       */
      this.ffDesktopItemsPerView = ffDesktopItemsPerView || "auto";

      this.ffDesktopAutoplay = ffDesktopAutoplay;
      this.ffMobileAutoplay = ffMobileAutoplay;
      this.ffDesktopAutoplayDelay = ffDesktopAutoplayDelay;
      this.ffMobileAutoplayDelay = ffMobileAutoplayDelay;

      this.ffIsVertical = ffIsVertical;

      // pagination styles
      this.ffDesktopThumbsSelector = ffDesktopThumbsSelector;
      this.ffMobileThumbsSelector = ffMobileThumbsSelector;

      this.ffNavigationType = ffNavigationType;
      this.ffLeftArrowClass = ffLeftArrowClass;
      this.ffRightArrowClass = ffRightArrowClass;

      this.ffDotsClass = ffDotsClass;
      this.ffDotClass = ffDotClass;
      this.ffDotActiveClass = ffDotActiveClass;

      this.ffDisabledArrowClass = ffDisabledArrowClass;

      this.ffMobileSlidesGap = ffMobileSlidesGap || "10";
      this.ffDesktopSlidesGap = ffDesktopSlidesGap || "14";

      this.ffCenteredSlides = ffCenteredSlides;

      this.ffInit();
    }

    //This slider has the option to display the arrows and dots outside the slides, but if there aren't enough slides for pagination they will not be displayed,
    //still there will be an empty space that was reserved for their display.
    //Removes empty spaces generated for the arrows and dots if they aren't being displayed.
    ffRestrains() {
      /**
       * @type {HTMLButtonElement | null}
       */
      const ffLeftArrow = this.querySelector("." + this.ffLeftArrowClass);
      /**
       * @type {HTMLButtonElement | null}
       */
      const ffRightArrow = this.querySelector("." + this.ffRightArrowClass);

      if (
        (ffLeftArrow?.disabled && ffRightArrow?.disabled) ||
        this.querySelector(".swiper-pagination-lock")
      ) {
        this.classList.remove("old-theme-ff-slider-with-arrows");
        this.classList.remove("old-theme-ff-slider-with-dots--mobile");
        this.classList.remove("old-theme-ff-slider-with-dots--desktop");
      }
    }

    ffResizeSlides() {
      let highestHeight = 0;

      this.querySelectorAll(".swiper-slide").forEach(({ clientHeight }) => {
        if (clientHeight > highestHeight) {
          highestHeight = clientHeight;
        }
      });

      this.style.setProperty("--ff-slide-resize-height", highestHeight + "px");
    }

    async ffInit() {
      const swiperParams = {
        grabCursor: true,
        mousewheel: false,
        spaceBetween: this.ffMobileSlidesGap,
        slidesPerView: this.ffMobileItemsPerView,
        autoplay: true,
      };

      if (this.ffIsVertical == "true") {
        swiperParams.direction = "vertical";
      }

      if (this.dataset.ffLoop == "true") {
        swiperParams.loop = true;
      }

      if (this.ffMobileBreakpoint) {
        //breakpoint to use desktop settings
        swiperParams.breakpoints = {
          [this.ffMobileBreakpoint]: {
            slidesPerView: this.ffDesktopItemsPerView,
            spaceBetween: this.ffDesktopSlidesGap,
          },
        };

        if (this.ffDesktopAutoplay == "true") {
          swiperParams.breakpoints[this.ffMobileBreakpoint].autoplay = {
            delay: this.ffDesktopAutoplayDelay,
            disabledOnInteraction: true,
          };
        } else {
          swiperParams.breakpoints[this.ffMobileBreakpoint].autoplay = false;
        }
      }

      if (this.ffMobileAutoplay == "true") {
        swiperParams.breakpoints[100] = {
          autoplay: {
            delay: this.ffMobileAutoplayDelay,
            disabledOnInteraction: true,
          },
        };
      } else {
        swiperParams.breakpoints[100] = {
          autoplay: false,
        };
      }

      if (this.ffNavigationType?.includes("dots")) {
        swiperParams.pagination = {
          el: `.${this.ffDotsClass}`,
          bulletClass: `${this.ffDotClass}`,
          bulletActiveClass: `${this.ffDotActiveClass}`,
          type: "bullets",
          clickable: true,
        };
      }

      if (this.ffNavigationType?.includes("arrows")) {
        swiperParams.navigation = {
          prevEl: `.${this.ffLeftArrowClass}`,
          nextEl: `.${this.ffRightArrowClass}`,
          disabledClass: `${this.ffDisabledArrowClass}`,
        };
      }

      let initSwiper = true;
      if (
        window.matchMedia(`(max-width:${this.ffMobileBreakpoint}px)`).matches
      ) {
        if (this.ffMobileThumbsSelector) {
          /**
           * @type {FfSlider | null}
           */
          const thumbsSwiper = document.querySelector(
            this.ffMobileThumbsSelector
          );

          if (thumbsSwiper) {
            swiperParams.thumbs = {
              swiper: thumbsSwiper.ffSwiper,
            };
          } else {
            console.error(
              `There wasn't found an old-theme-ff-slider or its swiper instance with the selector: '${this.ffMobileThumbsSelector}, which is needed for the creation of the thumbs.`
            );
          }
        } else {
          if (this.ffDesktopThumbsSelector) {
            document
              .querySelector(this.ffDesktopThumbsSelector)
              ?.remove();
          }
        }

        if (this.ffMobileDisabled == "true") {
          initSwiper = false;
        } else {
          this.querySelectorAll("[ff-remove-slide--mobile]").forEach(
            (slideToRemove) => {
              slideToRemove.remove();
            }
          );
        }
      } else {
        if (this.ffDesktopThumbsSelector) {
          /**
           * @type {FfSlider | null}
           */
          const thumbsSwiper = document.querySelector(
            this.ffDesktopThumbsSelector
          );

          if (thumbsSwiper) {
            swiperParams.thumbs = {
              swiper: thumbsSwiper.ffSwiper,
            };
          } else {
            console.error(
              `There wasn't found an old-theme-ff-slider or its swiper instance with the selector: '${this.ffDesktopThumbsSelector}, which is needed for the creation of the thumbs.`
            );
          }
        } else {
          if (this.ffMobileThumbsSelector) {
            document
              .querySelector(this.ffMobileThumbsSelector)
              ?.remove();
          }
        }

        if (this.ffDesktopDisabled == "true") {
          initSwiper = false;
        } else {
          this.querySelectorAll("[ff-remove-slide--desktop]").forEach(
            (slideToRemove) => {
              slideToRemove.remove();
            }
          );
        }

        if (this.ffCenteredSlides == "true") {
          swiperParams.centeredSlides = true;
        }
      }

      if (initSwiper) {
        this.ffSwiper = new Swiper(this.dataset.ffSwiperSelector, swiperParams);
        this.classList.remove("old-theme-ff-slider-uninitialized");
      }

      if (this.ffNotHideNav != "true") {
        this.ffRestrains();
      }
    }

    ffShopifySectionLoadHandler() {
      if (this.ffShouldResizeSlides == "true") {
        this.querySelectorAll(".swiper-slide").forEach((slide) => {
          slide.style.height = "";
        });

        setTimeout(() => {
          this.ffResizeSlides();
        }, 200);
      }
    }

    ffResizeHandler = () => {
      this.ffInit().then(() => this.ffShopifySectionLoadHandler());
    };
  }

  customElements.define("old-theme-ff-slider", FfSlider);
}

if (Shopify.designMode) {
  document.addEventListener("shopify:section:load", () => {
    requestAnimationFrame(() => {
      document.querySelectorAll("old-theme-ff-slider").forEach((slider) => {
        slider.ffShopifySectionLoadHandler();
      });
    });
  });
}
